import { useMemo, useState, type ReactNode } from 'react';
import type { Endpoint } from '../../features/catalog/model';
import { DataRequestBox } from '../../features/feedback/DataRequestBox';
import { responseTypeName } from '../../features/request-console/useSchemaForm';
import { CodeHighlight } from '../../components/CodeHighlight';
import { CopyButton } from '../../components/CopyButton';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import type { FieldRow } from '../../lib/endpointFields';
import { outlineSchema, toTypeScript } from '../../lib/schemaOutline';
import { FieldCard } from './FieldCard';
import { m } from '../../paraglide/messages';

type View = 'fields' | 'ts';

export interface ResponseCardProps {
  endpoint: Endpoint;
  outputSchema?: unknown;
  title: string;
  caption: string;
  /** a logo at the left of the header */
  mark?: ReactNode;
  rows: FieldRow[];
  requirable: Set<string>;
}

/**
 * The response fields, with the same shape as a TypeScript
 * interface one tab away, and the place to say a field is missing.
 */
export const ResponseCard = ({
  endpoint,
  outputSchema,
  title,
  caption,
  mark,
  rows,
  requirable,
}: ResponseCardProps) => {
  const ts = useMemo(() => {
    const nodes = outlineSchema(outputSchema);
    return nodes.length > 0
      ? toTypeScript(nodes, responseTypeName(endpoint.sku))
      : null;
  }, [outputSchema, endpoint.sku]);
  const [view, setView] = useState<View>('fields');
  const showTs = ts !== null && view === 'ts';

  return (
    <FieldCard
      title={title}
      caption={caption}
      mark={mark}
      rows={rows}
      requirable={requirable}
      actions={
        ts ? (
          <Tabs value={view} onValueChange={(v) => setView(v as View)}>
            <TabsList variant="line" className="h-auto p-0">
              <TabsTrigger value="fields" className="px-2.5 py-1 text-13">
                Fields
              </TabsTrigger>
              <TabsTrigger value="ts" className="px-2.5 py-1 text-13">
                TypeScript
              </TabsTrigger>
            </TabsList>
          </Tabs>
        ) : undefined
      }
      replacement={
        showTs ? (
          <div className="mt-4 flex flex-col items-start gap-3">
            <pre className="mono w-full overflow-x-auto rounded-10 bg-surface-2 px-4 py-3 text-12 leading-relaxed whitespace-pre-wrap break-words text-text-2">
              <code>
                <CodeHighlight code={ts} lang="ts" variant="card" />
              </code>
            </pre>
            <CopyButton value={ts} label={m.console_copy_interface()} />
          </div>
        ) : undefined
      }
      footer={
        <DataRequestBox
          label={m.endpoint_try_missing_label()}
          placeholder={m.endpoint_try_missing_placeholder({ name: endpoint.action })}
          sku={endpoint.sku}
        />
      }
    />
  );
};
